import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { environment } from "@web/config/environment";
import { authStorage } from "@features/auth/lib/auth-storage";
import type { AuthSession } from "@features/auth/types/types";
import { ApiError, type ApiResponse } from "@lib/api/api-types";
import { formatPersonName } from "@web/utils/formatters";

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };
export const apiClient = axios.create({
  baseURL: environment.apiBaseUrl,
  timeout: environment.requestTimeoutMs,
  headers: { Accept: "application/json" },
});
let refreshPromise: Promise<string> | null = null;

const personNameFields = new Set([
  "actorName",
  "actor_name",
  "agentName",
  "agent_name",
  "applicantName",
  "applicant_name",
  "approved_by_name",
  "assigned_by_name",
  "assignedToName",
  "assigned_to_name",
  "assignee_name",
  "createdByName",
  "created_by_name",
  "customerName",
  "customer_name",
  "listedByName",
  "listed_by_name",
  "owner_name",
  "performedByName",
  "performed_by_name",
  "rejected_by_name",
  "reviewedByName",
  "reviewed_by_name",
  "updated_by_name",
  "userName",
  "user_name",
]);

const personContainers = new Set([
  "agent",
  "agents",
  "approver",
  "assignedTo",
  "assignee",
  "createdBy",
  "customer",
  "listedBy",
  "owner",
  "performedBy",
  "reviewer",
  "salesPeople",
  "staff",
  "updatedBy",
  "user",
]);

function normalizeNestedPersonNames(
  value: unknown,
  container?: string,
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeNestedPersonNames(item, container));
  }
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => {
      if (
        typeof child === "string" &&
        (personNameFields.has(key) ||
          (key === "name" &&
            Boolean(container && personContainers.has(container))))
      ) {
        return [key, formatPersonName(child)];
      }
      return [key, normalizeNestedPersonNames(child, key)];
    }),
  );
}

function normalizeDirectPersonName(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeDirectPersonName);
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  return {
    ...record,
    ...(typeof record.name === "string"
      ? { name: formatPersonName(record.name) }
      : {}),
  };
}

function normalizeResponseNames(url: string | undefined, payload: unknown) {
  const normalized = normalizeNestedPersonNames(payload) as Record<
    string,
    unknown
  >;
  if (!normalized || typeof normalized !== "object") return normalized;
  const apiData = normalized.data;
  const isDirectPersonEndpoint =
    url === "/users" ||
    /^\/users\/\d+$/.test(url || "") ||
    url === "/profile" ||
    url === "/profile/summary" ||
    url === "/auth/login" ||
    url === "/auth/refresh-token";
  return isDirectPersonEndpoint
    ? { ...normalized, data: normalizeDirectPersonName(apiData) }
    : normalized;
}

apiClient.interceptors.request.use((config) => {
  const token = authStorage.accessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

async function refreshAccessToken() {
  const session = authStorage.get();
  if (!session?.refreshToken)
    throw new ApiError("Your session has expired.", 401);
  const response = await axios.post<ApiResponse<Partial<AuthSession>>>(
    `${environment.apiBaseUrl}/auth/refresh-token`,
    { refreshToken: session.refreshToken },
    { timeout: environment.requestTimeoutMs },
  );
  const accessToken = response.data.data.accessToken;
  if (!accessToken)
    throw new ApiError("Your session could not be refreshed.", 401);
  authStorage.set({ ...session, ...response.data.data, accessToken });
  return accessToken;
}

apiClient.interceptors.response.use(
  (response) => {
    if (["blob", "arraybuffer"].includes(response.config.responseType || ""))
      return response;
    response.data = normalizeResponseNames(response.config.url, response.data);
    return response;
  },
  async (
    error: AxiosError<{ message?: string; details?: unknown; code?: string }>,
  ) => {
    const config = error.config as RetryConfig | undefined;
    if (
      error.response?.status === 401 &&
      config &&
      !config._retry &&
      !config.url?.includes("/auth/")
    ) {
      config._retry = true;
      try {
        refreshPromise ??= refreshAccessToken().finally(
          () => (refreshPromise = null),
        );
        config.headers.Authorization = `Bearer ${await refreshPromise}`;
        return apiClient(config);
      } catch {
        authStorage.clear();
        window.location.assign("/login?reason=session-expired");
      }
    }
    const message =
      error.response?.data?.message ||
      (error.code === "ECONNABORTED"
        ? "The request timed out. Please try again."
        : "Unable to complete the request.");
    return Promise.reject(
      new ApiError(
        message,
        error.response?.status,
        error.response?.data?.details,
        error.response?.data?.code,
      ),
    );
  },
);
